import { useState, useEffect } from 'react';
import { changePassword } from '../api';
import { PASSWORD_RULE } from '../utils/userRoles';
import PasswordInput from './PasswordInput';

/* W-053 step 3a (2026-10-07) -- THE ONE PASSWORD FORM. Used by the forced "Set your new password" screen and by Change
   password (next to Sign out) -- one component, so the two cannot drift (Principle 0).

   The ONLY rule checked here is that the two new passwords match ("nothing sent" otherwise). Every other rule belongs to the
   server and its message is shown as it comes (TOO_SHORT / TOO_LONG / SAME_AS_CURRENT / WRONG_CURRENT_PASSWORD / the 429
   lockout / CHANGED_ELSEWHERE). The rule text shown is PASSWORD_RULE (a wall-mirror copy of the server's).

   On success changePassword() has ALREADY stored the new token + user (api.js) before onSuccess runs, so the next request
   carries the new token. The passwords live only in this component's state and are never logged or stored.

   W-053 step 3a.2: each field is the ONE PasswordInput (an eye button shows / hides that field only). After a SUCCESSFUL
   change every field goes back to hidden (hideSignal); a refused change leaves the user's choice as it is. */

const FONT = "'DM Sans',-apple-system,sans-serif";
const label = { display: 'block', fontSize: 13, color: '#555', marginBottom: 4 };
const input = { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' };

export const MISMATCH_MESSAGE = "The two passwords don't match.";

export default function PasswordChangeForm({ currentLabel = 'Current password', submitLabel = 'Change password', onSuccess, onCancel, onDirtyChange }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [hideSignal, setHideSignal] = useState(0);
    const dirty = !!(current || next || confirm);
    useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

    const submit = async e => {
        e.preventDefault();
        if (busy) return;
        setError('');
        if (next !== confirm) { setError(MISMATCH_MESSAGE); return; }
        setBusy(true);
        try {
            const res = await changePassword(current, next);
            setCurrent(''); setNext(''); setConfirm('');
            setHideSignal(n => n + 1);
            setBusy(false);
            if (onSuccess) onSuccess(res.data.user);
        } catch (err) {
            setError(err.response?.data?.error || 'The password could not be changed. Please try again.');
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} style={{ fontFamily: FONT }} data-testid="password-change-form">
            <p data-testid="password-rule" style={{ fontSize: 13, color: '#666', margin: '0 0 16px' }}>{PASSWORD_RULE}</p>
            {error && <div role="alert" style={{ background: '#fee', color: '#c00', padding: '8px 12px', borderRadius: 4, marginBottom: 16, fontSize: 13 }}>{error}</div>}
            <div style={{ marginBottom: 14 }}>
                <label style={label} htmlFor="pwf-current">{currentLabel}</label>
                <PasswordInput id="pwf-current" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} style={input} required hideSignal={hideSignal} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={label} htmlFor="pwf-new">New password</label>
                <PasswordInput id="pwf-new" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} style={input} required hideSignal={hideSignal} />
            </div>
            <div style={{ marginBottom: 20 }}>
                <label style={label} htmlFor="pwf-confirm">Confirm new password</label>
                <PasswordInput id="pwf-confirm" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} style={input} required hideSignal={hideSignal} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {onCancel && <button type="button" onClick={onCancel} disabled={busy}
                    style={{ padding: '10px 20px', background: '#fff', border: '1px solid #D0D5DD', borderRadius: 4, fontSize: 14, cursor: 'pointer' }}>Cancel</button>}
                <button type="submit" disabled={busy}
                    style={{ padding: '10px 20px', background: '#2A6DB0', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                    {busy ? 'Saving...' : submitLabel}
                </button>
            </div>
        </form>
    );
}
